import {focusableWithin, deepActiveElement} from '../utils/focusWithin';
import { useCallback, useEffect, useId, useRef, useState } from "react";

export function Icon({ name, size = 18 }) {
  const paths = {
    search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 4.5 4.5" /></>,
    refresh: <><path d="M20 7v5h-5M4 17v-5h5" /><path d="M6 7a7 7 0 0 1 12-2l2 3M4 16l2 3a7 7 0 0 0 12-2" /></>,
    plus: <path d="M12 5v14M5 12h14" />,
    close: <path d="m6 6 12 12M6 18 18 6" />,
    edit: <><path d="m15 5 4 4M4 20l5-1L20 8a2.8 2.8 0 0 0-4-4L5 15Z" /></>,
    next: <path d="m9 5 7 7-7 7" />,
    previous: <path d="m15 5-7 7 7 7" />,
    eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></>,
    copy: <><rect x="8" y="8" width="12" height="13" rx="2" /><path d="M15 8V3H3v13h5" /></>,
    trash: <><path d="M3 6h18M9 6V3h6v3M6 6l1 15h10l1-15M10 10v7M14 10v7" /></>,
    alert: <><circle cx="12" cy="12" r="9" /><path d="M12 7v6M12 16v1" /></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] || paths.next}</svg>;
}

export function ActionButton({ children, icon, tone = "", className = "", ...props }) {
  return <button type="button" className={`sa-button ${tone ? `sa-button--${tone}` : ""} ${className}`} {...props}>
    {icon && <Icon name={icon} />}{children}
  </button>;
}

export function Notice({ children, success = false }) {
  return children ? <div className={`sa-notice ${success ? "sa-notice--success" : ""}`} role={success ? "status" : "alert"}>{children}</div> : null;
}

export function StatusBadge({ deleted }) {
  const inactive = Number(deleted) === 1;
  return <span className={`sa-badge ${inactive ? "sa-badge--inactive" : ""}`}><span />{inactive ? "Uninstalled" : "Installed"}</span>;
}

export function formatTime(value, withTime = false) {
  const date = new Date(Number(value) * 1000);
  if (!value || Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", year: "numeric", month: "short", day: "2-digit",
    ...(withTime ? { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false } : {}) }).format(date);
}

export function messageOf(error, fallback) {
  return error?.response?.data?.msg || error?.response?.data?.message || error?.message || fallback;
}

export function useRemoteList(request, failureMessage) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const generation = useRef(0);
  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setError("");
    try {
      const response = await request({});
      if (response.status !== 200 || !Array.isArray(response.data)) throw new Error(response.msg || failureMessage);
      if (current === generation.current) setItems(response.data);
    } catch (err) {
      if (current === generation.current) setError(messageOf(err, failureMessage));
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [request, failureMessage]);
  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load]);
  return { items, setItems, loading, error, load };
}

export function Pager({ page, pageSize, total, onChange, noun }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return <div className="sa-pager">
    <span>{total ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total} ${noun}` : `0 ${noun}`}</span>
    <div className="sa-actions">
      <ActionButton icon="previous" aria-label="Previous page" disabled={page <= 1} onClick={() => onChange(page - 1)} />
      <span className="sa-page-number" aria-label={`Page ${page} of ${pages}`}>{page} / {pages}</span>
      <ActionButton icon="next" aria-label="Next page" disabled={page >= pages} onClick={() => onChange(page + 1)} />
    </div>
  </div>;
}

export function Drawer({ title, subtitle, onClose, children, footer, busy = false }) {
  const id = useId();
  const ref = useRef(null);
  const state = useRef({ onClose, busy });
  state.current = { onClose, busy };
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.focus();
    const handleKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (!state.current.busy) state.current.onClose();
      }
      if (event.key === "Tab") {
        const nodes = focusableWithin(ref.current);
        const active = deepActiveElement();
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (!first) { event.preventDefault(); return; }
        if (event.shiftKey && (active === first || (document.activeElement === ref.current || !ref.current.contains(document.activeElement)))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (active === last || (document.activeElement === ref.current || !ref.current.contains(document.activeElement)))) {
          event.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", handleKey);
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return <div className="sa-drawer-layer">
    <div className="sa-drawer-backdrop" onClick={() => !busy && onClose()} />
    <section ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={id} aria-busy={busy} className="sa-drawer">
      <header className="sa-drawer-header"><div><h2 id={id}>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
        <ActionButton icon="close" aria-label="Close panel" disabled={busy} onClick={onClose} />
      </header>
      <div className="sa-drawer-body">{children}</div>
      <footer className="sa-drawer-footer">{footer}</footer>
    </section>
  </div>;
}
